import { useEffect } from 'react';
import { useCanvasContext } from '../contexts/CanvasContext';
import { useSessionContext } from '../contexts/SessionContext';
import { useTokenContext } from '../contexts/TokenContext';
import { useSheetContext } from '../contexts/SheetContext';
import { computeControlledTokenIds, sameIdSet } from '../lib/controlled-tokens';

/**
 * Keeps `canvasCtx.controlledTokenIdsRef` in sync with the LIVE stores:
 * TokenContext tokens, SheetContext sheets, the current participant and the
 * live session settings. Must be mounted below TokenProvider and SheetProvider
 * (App.tsx). Replaces the effect that used to live in CanvasContext and read
 * the frozen join snapshot + the page-store cache — the "F5 fixes it" class.
 *
 * Consumers keep reading the same ref (CanvasArea drag gate, MobileCanvas,
 * useDynamicFog vision filter); only the writer moved.
 */
export function useControlledTokensSync(): void {
  const { controlledTokenIdsRef, dynamicFog } = useCanvasContext();
  const { mp, sessionSettings } = useSessionContext();
  const { tokens } = useTokenContext();
  const { sheets } = useSheetContext();

  const participant = mp.participant;
  const liveTokens = tokens.tokens;
  const liveSheets = sheets.sheets;
  const moveOtherTokens = sessionSettings.playerPermissions?.moveOtherTokens === true;

  useEffect(() => {
    const next = computeControlledTokenIds({
      participant,
      sheets: liveSheets,
      tokens: liveTokens,
      moveOtherTokens,
    });
    if (sameIdSet(controlledTokenIdsRef.current, next)) return;
    controlledTokenIdsRef.current = next;
    // Vision filter in dynamic fog depends on the controlled set.
    dynamicFog.invalidateVisibility();
  }, [participant, liveSheets, liveTokens, moveOtherTokens, controlledTokenIdsRef, dynamicFog]);
}

/** Null-rendering mount point for `useControlledTokensSync`. */
export function ControlledTokensSync(): null {
  useControlledTokensSync();
  return null;
}
